import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ClientServiceCreateResponseJson,
  ClientServiceCreateResponseSchema,
  ClientServiceListRequestJson,
  ClientServiceListRequestSchema,
  CollectionNames,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { ClientRepositoryService, withSearchSync } from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedClientActions } from './paginated-client.actions';

@Injectable()
export class PaginatedClientEffects extends AuthRunEffects {
  private readonly injector = inject(EnvironmentInjector);
  private readonly errors = inject(GlobalErrorHandlerService);
  private readonly repository = inject(ClientRepositoryService);
  private readonly notifications = inject(NbToastrService);
  private readonly utilities = inject(UtilitiesFacade);

  listPaginatedClients$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedClientActions.listPaginatedClients,
        PaginatedClientActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedClientActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: ClientServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.Clients, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(ClientServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.Users);
            this.notifications.warning(
              'There was an error with the search server for fetching clients.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedClientActions.listPaginatedClientsSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    ClientServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as ClientServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(PaginatedClientActions.listPaginatedClientsFailure(error));
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    super(inject(Actions));
  }
}
